import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { http } from "../../lib/api";
import PremiumLayout from "./PremiumLayout";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=2200&auto=format&fit=crop&q=80";

export default function PremiumHome() {
  const [projects, setProjects] = useState([]);
  const [company, setCompany] = useState(null);
  const [latestBlog, setLatestBlog] = useState([]);
  const [landing, setLanding] = useState({});
  const [hero, setHero] = useState({ image_url: "", headline: "", subheadline: "" });

  useEffect(() => {
    http.get("/marketing/site").then(({ data }) => {
      setProjects((data.projects || []).slice(0, 4));
      setCompany(data.company);
      setLatestBlog(data.latest_blog || []);
      setHero(data.hero || {});
    }).catch(() => {});
    http.get("/marketing/landing").then(({ data }) => setLanding(data || {})).catch(() => {});
  }, []);

  // Hero image comes from CMS upload → /api/marketing/asset/…; fall back to the
  // default Unsplash still if the studio has never uploaded a hero yet.
  const heroSrc = hero.image_url || DEFAULT_HERO;

  const numbers = landing.landing_numbers?.items || [
    { k: "Since", v: "2014", sub: "Practice established" },
    { k: "Turnkey", v: "100+", sub: "Projects delivered" },
    { k: "Cities", v: "12", sub: "Across India" },
    { k: "Studio", v: "18", sub: "Architects & engineers" },
  ];
  const expertise = landing.landing_expertise || {};
  const expertiseTitle = expertise.title || "Three studios";
  const expertiseTitleItalic = expertise.title_italic || "— one philosophy.";
  const expertiseIntro = expertise.intro || "Whether it's the raw civil frame, the interior architecture that softens it, or the complete turnkey handover — every stage is drawn, engineered, and installed by the same studio. No handoffs, no compromises.";
  const expertiseCards = expertise.cards || [
    { k: "01", t: "Civil & Structural", d: "Ground-up construction with in-house structural engineering, RCC design, and quality-controlled execution." },
    { k: "02", t: "Interior Architecture", d: "Bespoke interior design — modular kitchens, false ceilings, custom joinery, and lighting design." },
    { k: "03", t: "Turnkey Projects", d: "Concept to handover: architecture + structure + interiors + landscape, delivered as one contract." },
  ];
  const cta = landing.landing_cta || {};
  const ctaOverline = cta.overline || "Consultation";
  const ctaTitle = cta.title || "Let's draw";
  const ctaTitleItalic = cta.title_italic || "yours.";
  const ctaCopy = cta.copy || "A short conversation is usually all it takes to know if we're a fit. No commitment, no rush — just an honest look at what your project could be.";
  const ctaBtn = cta.button_label || "Book a Design Session →";
  const ctaHref = cta.button_href || "/contact";

  return (
    <PremiumLayout hero>
      {/* ------- HERO ------- */}
      <section className="relative h-[92vh] w-full overflow-hidden" data-testid="premium-hero">
        <img src={heroSrc} alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/40 to-[#0A0A0A]" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-24">
          <div className="mono gold fade-up">Since 2014 · Architecture &amp; Interior Design</div>
          <h1 className="serif text-[54px] md:text-[92px] leading-[0.98] mt-4 max-w-4xl fade-up cream">
            Homes that hold <span className="gold italic">a lifetime</span> of quiet elegance.
          </h1>
          <p className="mt-6 max-w-2xl text-[#DDD5C4] text-lg leading-relaxed fade-up-slow">
            Full-service civil, interior and turnkey studios — building spaces that respect
            proportion, material, and the way you actually live.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 fade-up-slow">
            <Link to="/projects" data-testid="hero-view-portfolio"
                  className="mono border border-[#C9A96E] gold px-8 py-4 hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-colors">
              View Portfolio →
            </Link>
            <Link to="/contact" data-testid="hero-book-session"
                  className="mono border border-[#F5F1EA]/40 cream px-8 py-4 hover:border-[#F5F1EA]">
              Book a Design Session
            </Link>
          </div>
        </div>
        {/* Scroll cue */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <div className="mono text-[#7A7267] text-[10px]">SCROLL</div>
          <div className="w-px h-8 bg-gradient-to-b from-[#C9A96E] to-transparent" />
        </div>
      </section>

      {/* ------- CORE NUMBERS STRIP ------- */}
      <section className="border-y border-[#1F1F1F]" data-testid="premium-numbers">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
          {numbers.map((n, i) => (
            <div key={`${n.k}-${i}`} className="fade-up">
              <div className="mono text-[#7A7267]">{n.k}</div>
              <div className="serif text-5xl md:text-6xl gold mt-2 leading-none">{n.v}</div>
              <div className="text-sm text-[#B8B0A0] mt-3" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(n.sub || "") }} />
            </div>
          ))}
        </div>
      </section>

      {/* ------- EXPERTISE TEASER (editable) ------- */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24" data-testid="premium-expertise-teaser">
        <div className="grid md:grid-cols-2 gap-16 items-end">
          <div>
            <div className="mono gold">02 · Expertise</div>
            <h2 className="serif text-5xl md:text-6xl mt-4 leading-[1.05] cream">
              {expertiseTitle}<br />
              <span className="gold italic">{expertiseTitleItalic}</span>
            </h2>
          </div>
          <p className="text-[#B8B0A0] text-lg leading-relaxed">{expertiseIntro}</p>
        </div>
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          {expertiseCards.map((c, i) => (
            <Link key={`${c.k}-${i}`} to="/expertise" data-testid={`exp-card-${c.k}`}
                  className="group block border border-[#1F1F1F] p-8 hover:border-[#C9A96E] transition-colors">
              <div className="mono gold">{c.k}</div>
              <div className="serif text-2xl mt-4 cream" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.t || "") }} />
              <div className="text-sm text-[#B8B0A0] mt-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.d || "") }} />
              <div className="mono gold mt-8 group-hover:translate-x-1 transition-transform">Learn more →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* ------- FEATURED PORTFOLIO — asymmetric peek ------- */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24" data-testid="premium-featured">
        <div className="flex items-end justify-between gap-6">
          <div>
            <div className="mono gold">03 · Featured Work</div>
            <h2 className="serif text-5xl md:text-6xl mt-4 leading-[1.05] cream">
              Selected <span className="gold italic">projects.</span>
            </h2>
          </div>
          <Link to="/projects" className="mono gold hidden md:inline-block">View all →</Link>
        </div>
        <div className="mt-14 grid md:grid-cols-6 gap-6">
          {projects[0] && (
            <FeaturedTile p={projects[0]} className="md:col-span-4 md:row-span-2 aspect-[4/3]" />
          )}
          {projects[1] && <FeaturedTile p={projects[1]} className="md:col-span-2 aspect-square" />}
          {projects[2] && <FeaturedTile p={projects[2]} className="md:col-span-2 aspect-square" />}
          {projects[3] && <FeaturedTile p={projects[3]} className="md:col-span-6 aspect-[16/6]" />}
        </div>
      </section>

      {/* ------- LATEST FROM THE STUDIO (blog) ------- */}
      {latestBlog.length > 0 && (
        <section className="border-t border-[#1F1F1F]" data-testid="premium-latest-blog">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
            <div className="flex items-end justify-between gap-6">
              <div>
                <div className="gold text-[11px] tracking-[.16em] uppercase">Studio Journal</div>
                <h2 className="text-5xl md:text-6xl mt-4 leading-[1.05]">
                  Latest from <span className="gold italic">the studio.</span>
                </h2>
              </div>
              <Link to="/blog" className="hidden md:inline-block gold border-b border-[#C9A96E]/40 hover:border-[#C9A96E]">
                All journal notes →
              </Link>
            </div>
            <div className="mt-12 grid md:grid-cols-2 gap-6">
              {latestBlog.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`}
                      data-testid={`home-blog-${p.slug}`}
                      className="group border border-[#1F1F1F] overflow-hidden hover:border-[#C9A96E] transition-colors">
                  {p.cover_image_url && (
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={p.cover_image_url} alt={p.title}
                           className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" />
                    </div>
                  )}
                  <div className="p-7">
                    <div className="flex items-center justify-between text-[11px] tracking-[.16em] uppercase text-[#9A9284]">
                      <span className="gold">{p.tag || "Journal"}</span>
                      <span>{new Date(p.published_at || p.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
                    </div>
                    <div className="text-2xl md:text-3xl mt-4 leading-tight">{p.title}</div>
                    {p.excerpt && <p className="mt-4 text-[#B8B0A0] text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>}
                    <div className="mt-5 gold text-sm">Read the note →</div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-8 md:hidden">
              <Link to="/blog" className="gold border-b border-[#C9A96E]/40">All journal notes →</Link>
            </div>
          </div>
        </section>
      )}

      {/* ------- CTA BAND (editable) ------- */}
      <section className="border-t border-[#1F1F1F]" data-testid="premium-cta-band">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="mono gold">{ctaOverline}</div>
            <h2 className="serif text-5xl md:text-6xl leading-[1.05] mt-4 cream">
              {ctaTitle} <span className="gold italic">{ctaTitleItalic}</span>
            </h2>
          </div>
          <div>
            <p className="text-[#B8B0A0] text-lg leading-relaxed">{ctaCopy}</p>
            <Link to={ctaHref} data-testid="cta-book-band"
                  className="inline-block mt-8 mono border border-[#C9A96E] gold px-8 py-4 hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-colors">
              {ctaBtn}
            </Link>
          </div>
        </div>
      </section>
    </PremiumLayout>
  );
}

function FeaturedTile({ p, className = "" }) {
  const cover = (p.photos || [])[0] || "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1400&auto=format&fit=crop&q=80";
  return (
    <Link to={`/projects/${p.id}`} data-testid={`featured-tile-${p.id}`}
          className={`relative group overflow-hidden border border-[#1F1F1F] ${className}`}>
      <img src={cover} alt={p.title}
           className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-6">
        <div className="mono gold">{p.category || "Project"} · {p.location || "—"}</div>
        <div className="serif text-2xl md:text-3xl cream mt-2">{p.title}</div>
      </div>
    </Link>
  );
}
